import QRCode, { QRCodeSegment } from 'qrcode'
import JSZip from 'jszip'
import { ProductCombinationType } from '@/__generated__/globalTypes'
import { Canvas, createCanvas, loadImage } from 'canvas'

export interface QrCodeCanvasData {
  mode: QrCodeMode
  productPackageId: string
  combinationType: ProductCombinationType
  productSetIds: string[]
}

export interface QrCodeCardData {
  mode: QrCodeMode
  qrCodeCanvasDataUrl: string
  combinationName?: string
  categoryName?: string
  productSetName?: string
  brandName?: string
  seriesName?: string
}

export enum QrCodeMode {
  // For a single product set
  SINGLE = 'SINGLE',
  // For multiple product sets, with all product set ids encoded in a single QR code, downloaded as a single image file
  COMBINATION = 'COMBINATION',
  // For a room, with all product set ids in that room encoded in a single QR code, downloaded as a single image file
  ROOM = 'ROOM'
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDateString(): string {
  return new Date().toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

// Splits `text` into the minimum number of lines that fit within `maxWidth`,
// then binary-searches for the narrowest target width that still yields that
// same line count — producing roughly equal-length (balanced) lines.
function wrapTextBalanced(
  ctx: { measureText: (text: string) => { width: number } },
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(' ')

  // Greedy line-breaking for a given target width (not necessarily `maxWidth`)
  function greedyLines(targetWidth: number): string[] {
    const lines: string[] = []
    let current = ''
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word
      if (ctx.measureText(candidate).width > targetWidth && current) {
        lines.push(current)
        current = word
      } else {
        current = candidate
      }
    }
    if (current) lines.push(current)
    return lines
  }

  const minLines = greedyLines(maxWidth).length
  if (minLines <= 1) return [text]

  // Find the smallest target width that still fits in `minLines` lines
  let lo = 0
  let hi = maxWidth
  while (hi - lo > 1) {
    const mid = Math.round((lo + hi) / 2)
    if (greedyLines(mid).length <= minLines) hi = mid
    else lo = mid
  }
  return greedyLines(hi)
}

// ── QR code canvas generation ─────────────────────────────────────────────────

export async function generateQrCodeCanvasDataUrl(data: QrCodeCanvasData) {
  const qrCodeUrl = getQrCodeUrl(data)

  const size = 650
  const iconArea = 173.3
  const iconSize = 130
  const iconAreaX = (size - iconArea) / 2
  const iconAreaY = (size - iconArea) / 2

  const canvas = createCanvas(size, size)
  const ctx = canvas.getContext('2d')

  const qrCodeData = await QRCode.toDataURL(qrCodeUrl, {
    width: size,
    margin: 0,
    errorCorrectionLevel: 'H',
    color: { dark: '#000' }
  })

  // Make an image of the qr code data URL and draw it on the canvas
  const qrImage = await loadImage(qrCodeData)
  ctx.drawImage(qrImage, 0, 0, size, size)

  // Draw white square for the icon
  ctx.fillStyle = '#fff'
  ctx.fillRect(iconAreaX, iconAreaY, iconArea, iconArea)

  const { default: iconUrl } = await import(
    `../assets/product_combinations_png/${data.combinationType.toLowerCase()}.png`
  )
  const iconImage = await loadImage(iconUrl)

  const scale = iconImage.height > iconImage.width ? iconSize / iconImage.height : iconSize / iconImage.width
  const trueIconHeight = iconImage.height * scale
  const trueIconWidth = iconImage.width * scale

  // Draw the icon, centered in the white square
  ctx.drawImage(
    iconImage,
    iconAreaX + (iconArea - trueIconWidth) / 2,
    iconAreaY + (iconArea - trueIconHeight) / 2,
    trueIconWidth,
    trueIconHeight
  )

  return canvas.toDataURL()
}

function guidToDecimal(guid: string) {
  guid = guid.replaceAll('-', '')
  const bigInt = BigInt('0x' + guid)
  return bigInt.toString(10)
}

function getQrCodeUrl(data: QrCodeCanvasData) {
  switch (data.mode) {
    case QrCodeMode.SINGLE:
      return `${process.env.VUE_APP_APP_URL}/product-package/${data.productPackageId}/product-set/${data.productSetIds[0]}`
    case QrCodeMode.COMBINATION:
      return data.productSetIds.reduce(
        (acc: QRCodeSegment[], current, index) => {
          // Convert the guid to a decimal string to be able to use the more efficient numeric encoding mode in the QR code
          acc.push({
            data: guidToDecimal(current),
            mode: 'numeric'
          })
          // Add a comma as a separator between product set ids, except for the last one
          if (index !== data.productSetIds.length - 1) {
            acc.push({
              data: ','
            })
          }
          return acc
        },
        [
          {
            data: `${process.env.VUE_APP_APP_URL}/product-package/${data.productPackageId}?product-set-ids=`
          } as QRCodeSegment
        ]
      )
    default:
      throw new Error('Unsupported QR code mode: ' + data.mode)
  }
}

function getCardHeader(data: QrCodeCardData) {
  switch (data.mode) {
    case QrCodeMode.SINGLE:
      return data.categoryName ?? ''
    case QrCodeMode.COMBINATION:
      return data.combinationName ?? ''
    default:
      return ''
  }
}

async function convertCardToCanvas(data: QrCodeCardData): Promise<Canvas> {
  // A7 size at 300 DPI
  const width = 874
  const height = 1240
  const scale = 2.6 // scale: component CSS px → card px

  const canvas = createCanvas(width, height)
  const ctx = canvas.getContext('2d')

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, width, height)

  // Load QR code and logo in parallel
  const { default: logoSrc } = await import('@/assets/logo.svg')
  const qrImg = await loadImage(data.qrCodeCanvasDataUrl)
  const logoImg = await loadImage(logoSrc)

  ctx.fillStyle = '#000000'
  ctx.textBaseline = 'top'

  // ── QR code size (used for text alignment too) ────────────────────────────
  const qrSize = Math.round(220 * scale)
  const qrLeft = Math.round((width - qrSize) / 2)

  let topPadding = 130 // top padding, balanced against bottom

  // h2: font-size 20px, aligned with QR code left edge
  const headerSize = Math.round(20 * scale)
  ctx.font = `bold ${headerSize}px Montserrat, Arial, sans-serif`
  ctx.fillText(getCardHeader(data), qrLeft, topPadding)
  topPadding += Math.round(headerSize * 1.2)

  // span: font-size 17px, aligned with QR code left edge, balanced wrap
  const spanSize = Math.round(17 * scale)
  const spanLineHeight = Math.round(spanSize * 1.35)
  ctx.font = `${spanSize}px Montserrat, Arial, sans-serif`
  for (const line of wrapTextBalanced(ctx, data.brandName ?? '', qrSize)) {
    ctx.fillText(line, qrLeft, topPadding)
    topPadding += spanLineHeight
  }

  for (const line of wrapTextBalanced(ctx, data.seriesName ?? '', qrSize)) {
    ctx.fillText(line, qrLeft, topPadding)
    topPadding += spanLineHeight
  }
  // mb-4 = 21px → 54px (scaled)
  topPadding += Math.round(21 * scale)

  // ── QR code image ─────────────────────────────────────────────────────────
  // .c-qr-code__img: 220px → 528px, centered
  ctx.drawImage(qrImg, qrLeft, topPadding, qrSize, qrSize)
  topPadding += qrSize

  // ── Logo ──────────────────────────────────────────────────────────────────
  // my-6 = 24px margin-top → 58px (scaled)
  topPadding += Math.round(24 * scale)

  // .c-logo__image: width 220px → 528px, maintain aspect ratio
  const logoWidth = Math.round(220 * scale)
  const naturalWidth = logoImg.naturalWidth || logoImg.width
  const naturalHeight = logoImg.naturalHeight || logoImg.height
  const logoHeight =
    naturalWidth > 0 ? Math.round(naturalHeight * (logoWidth / naturalWidth)) : Math.round(logoWidth / 5)
  ctx.drawImage(logoImg, Math.round((width - logoWidth) / 2), topPadding, logoWidth, logoHeight)

  return canvas
}

// ── Single download (used by QrGenerator dialog) ──────────────────────────────
export async function generateQrCodeCardDataUrl(data: QrCodeCardData) {
  const cardCanvas = await convertCardToCanvas(data)
  return cardCanvas.toDataURL('image/png')
}

export function downloadQrCodeCard(dataUrl: string, filename: string) {
  downloadBlob(dataUrl, `${getDateString()}-${filename}`)
}

// ── Bulk download (used by Bulk download button ProductSetList) ──────────────────────────────
export async function downloadBulkAsZip(data: QrCodeCardData[], categoryName: string): Promise<void> {
  const zip = new JSZip()
  const dateString = getDateString()

  for (const item of data) {
    const canvas = await convertCardToCanvas(item)
    const canvasDataUrl = await fetch(canvas.toDataURL('image/png'))
    const blob = await canvasDataUrl.blob()
    zip.file(`${dateString}-dbk-qr-${item.productSetName}.png`, blob)
  }

  const content = await zip.generateAsync({ type: 'blob' })
  downloadBlob(URL.createObjectURL(content), `${categoryName}-${dateString}.zip`)
}

function downloadBlob(data: string, filename: string) {
  const a = document.createElement('a')
  a.href = data
  a.download = filename
  a.click()
  a.remove()
}
